<template>
    <div class="c-header-actions__item is-minicart">
        <a
            href="/nakupny-kosik"
            class="c-link"
            :class="{'is-active': isOpen}"
            :data-quantity="count"
            @click="handleMinicartClick"
        >
            <i class="ico ico-shopping-cart" aria-hidden="true"></i>
            <span class="is-text" v-if="isEmpty">{{ $t('Minicart.Trigger.title') }}</span>
            <span class="is-text" v-else>{{ $t('Minicart.Trigger.title') }}</span>
            <div class="c-badge is-primary" :data-count="count">{{ count }}</div>
        </a>

        <div :class="{
            'c-minicart': true,
            'is-loading': isLoading
        }" v-if="isOpen">
            <div class="c-minicart__products-wrapper">
                <div class="c-minicart__header">
                    <div class="is-title">
                        {{ $t('Minicart.Header.title') }}
                    </div>

                    <div class="c-msg c-msg--success" v-if="isInitialized && !isEmpty">
                        <i class="ico ico-delivery"></i>
                        <span v-html="freeShippingText"></span>
                    </div>
                </div>

                <template v-if="isInitialized">
                    <div class="c-minicart__products" v-if="!isEmpty">
                        <template v-for="item in items">
                            <MinicartItem
                                :ref="`cart_item_${item.cart_id}`"
                                :key="item.cart_id"
                                :item="item"
                                v-if="!item.cart_id.endsWith('-basic')"
                                :isUpdatingQuantity="updatingQuantity[item.cart_id]"
                                @remove="removeItem"
                                @spinningQuantity="spinningQuantity"
                                @updateQuantity="updateQuantity"
                            />
                        </template>
                    </div>

                    <div class="c-minicart__empty" v-else>
                        {{ $t('Minicart.emptyText') }}
                    </div>
                </template>
            </div>

            <div class="c-minicart__subtotal" v-if="isInitialized && !isEmpty">
                <div class="c-minicart-subtotal">
                    <div class="c-minicart-subtotal__item">
                        <span class="c-minicart-subtotal__key is-bigger">
                            {{ pricesWithVat ? $t('Minicart.priceWithVat') : $t('Minicart.priceWithoutVat') }}
                        </span>
                        <span
                            class="c-minicart-subtotal__value is-bigger"
                            v-html="formatPrice(pricesWithVat ? totalPrice : subtotalPrice)"
                        ></span>
                    </div>

                    <div class="c-minicart-subtotal__item h-is-red" v-if="savings > 0">
                        <span class="c-minicart-subtotal__text">
                            {{ $t('Minicart.savings') }} <strong v-html="formatPrice(savings)"></strong>
                        </span>
                    </div>
                </div>

                <a class="c-btn c-btn--cart has-size-45" href="/nakupny-kosik" :title="$t('Minicart.proceedToCheckout')">
                    {{ $t('Minicart.proceedToCheckout') }}
                    <i aria-hidden="true" class="ico ico-arrow-right"></i>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { showHeaderOverlay, hideHeaderOverlay } from 'Functions/overlay';
import RshopMinicart from "FrontendComponents/Minicart";
import { formatPrice } from "FrontendJsAssets/common/format";
import client from "FrontendJsAssets/common/client";
import { CancelToken } from "axios";
import { checkResolution } from 'Functions/helpers';
import { tippyAlert } from 'Functions/tippy';

const lang = document.querySelector('html').getAttribute('lang');
let locale;

import(`../../locale/${lang}.json`).then((module) => {
    locale = module.Swal;
});

export default {
    extends: RshopMinicart,
    components: {
        MinicartItem: () => import("./MinicartItem"),
    },
    created() {
        this.fetchCart();

        if (typeof rEvent.on === 'function') {
            rEvent.on('Cart.product.after_add', (response) => {
                this.fetchCart();
            });

            rEvent.on('Cart.addedProduct', (response) => {
                this.fetchCart();
            });

            rEvent.on('Cart.customFetchCart', () => {
                this.fetchCart();
            });

            rEvent.on('Cart.customCancelFetchCart', () => {
                this.cancelFetchCart();
            });
        }
    },
    data() {
        return {
            isOpen: false,
            pricesWithVat: window.Eshop.options.pricesWithVat,
            freeShipping: [],
            items: [],
            totals: [],
            fetchCancelToken: null,
            isInitialized: false,
            fetchingProducts: true,
            fetchingTotals: true,
            updatingQuantity: {},
            updatingQuantityTimeouts: {},
            updateQuantityCancelTokens: {},
        };
    },
    computed: {
        count() {
            let basicCount = 0

            for (const item of this.items) {
                if (item.cart_id.endsWith('-basic')) {
                    basicCount++
                }
            }

            return this.totals.count - basicCount || 0
        },
        itemsCount() {
            if (this.items) {
                return this.items.reduce((count, item) => count + parseInt(item.quantity), 0)
            }

            return 0
        },
        isEmpty() {
            return this.itemsCount === 0
        },
        totalPrice() {
            return this.totals.total
        },
        subtotalPrice() {
            return this.totals.subtotal
        },
        savings() {
            let tmpValue = 0;

            for (const item of this.items) {
                if (item.is_action) {
                    tmpValue += item.quantity * (this.pricesWithVat ? (item.old_price_vat - item.price_vat) : (item.old_price - item.price));
                }
            }

            return Math.abs(tmpValue);
        },
        hasFreeShipping() {
            return this.freeShipping ? this.freeShipping.free_shipping_amount <= 0 : false
        },
        freeShippingText() {
            if (!this.freeShipping) {
                return null
            }

            return this.freeShipping[this.hasFreeShipping ? 'free_shipping_text' : 'not_free_shipping_text']
        },
        isLoading() {
            return this.fetchingProducts || this.fetchingTotals || Object.keys(this.updatingQuantity).length > 0
        }
    },
    methods: {
        cancelFetchCart() {
            if (this.fetchCancelToken) {
                this.fetchCancelToken.cancel()
            }

            this.fetchCancelToken = CancelToken.source()
        },
        fetchCart() {
            this.cancelFetchCart()
            this.fetchProducts()
            this.fetchTotals()
        },
        fetchProducts() {
            this.fetchingProducts = true

            client
                .get("/cart/fetch-products", {
                    cancelToken: this.fetchCancelToken.token,
                })
                .then((response) => {
                    if (!response.data.status) {
                        return;
                    }

                    this.items = Object.values(response.data.data.items);

                    rEvent.dispatch('Cart.fetchedProducts', {
                        response: response.data
                    });
                })
                .catch((error) => {})
                .then(() => {
                    this.fetchingProducts = false
                });
        },
        fetchTotals() {
            this.fetchingTotals = true

            client
                .get("/cart/fetch-totals", {
                    cancelToken: this.fetchCancelToken.token,
                })
                .then((response) => {
                    if (!response.data.status) {
                        return;
                    }

                    this.freeShipping = response.data.data.free_shipping;
                    this.totals = response.data.data.totals;

                    rEvent.dispatch('Custom.Cart.fetchedTotals', {
                        response: response.data
                    });
                })
                .catch((error) => {})
                .then(() => {
                    this.fetchingTotals = false
                });
        },
        formatPrice(price) {
            return formatPrice(price);
        },
        removeItem(cartId) {
            let itemIndex = -1;

            // find item
            const item = this.items.find((item, index) => {
                if (item.cart_id === cartId) {
                    itemIndex = index;
                    return true;
                }

                return false;
            });

            if (itemIndex === -1) {
                return false;
            }

            // remove item
            window.Eshop.confirm(
                locale.DeleteProduct.title,
                {
                    denyButtonText: locale.DeleteProduct.deny,
                    confirmButtonText: locale.DeleteProduct.confirm
                },
                () => {
                    document.querySelector('.swal2-confirm').classList.add('is-loading');

                    return client.post(`/remove-item/${cartId}`).then((response) => {
                        window.rEvent.dispatch('Cart.product.removed', response.data);
                        window.rEvent.dispatch('Cart.removedProduct', {
                            response: response.data,
                            product: item
                        });
                    });
                },
                (result) => {
                    if (result.isConfirmed) {
                        this.items.splice(itemIndex, 1)
                        this.fetchCart()
                    }
                }
            );
        },
        spinningQuantity(cartId) {
            if (!this.updatingQuantity[cartId]) {
                return
            }

            this.$set(this.updatingQuantity, cartId, true)
            this.cancelUpdateQuantity(cartId)
            this.cancelFetchCart()

            if (this.updatingQuantityTimeouts[cartId]) {
                clearTimeout(this.updatingQuantityTimeouts[cartId])
            }
        },
        cancelUpdateQuantity(cartId) {
            const quantityCancelToken = this.updateQuantityCancelTokens[cartId]

            if (quantityCancelToken) {
                quantityCancelToken.cancel()
            }

            this.updateQuantityCancelTokens[cartId] = CancelToken.source()
        },
        updateQuantity(cartId, quantity) {
            let hasReachedMaxQuantity = false
            const isFirstUpdate = !this.updatingQuantity[cartId]
            const rSpinner = this.$refs[`cart_item_${cartId}`]
                ? this.$refs[`cart_item_${cartId}`][0].$refs['rSpinner']
                : null

            if (rSpinner) {
                const quantity = rSpinner.getParsedValue()
                const oldValue = rSpinner.getParsedOldValue()
                const max = rSpinner.max
                const spinDirection = rSpinner.spinDirection

                if (quantity == oldValue && quantity == max && spinDirection == 'up' || quantity == max && spinDirection === null) {
                    tippyAlert(
                        rSpinner,
                        window.customMessageProductQuantityNotAvailable.replaceAll(':productStock', rSpinner.max),
                        { theme: 'warning', zIndex: '200', offsetTop: 0 },
                        20 * 1000
                    );

                    hasReachedMaxQuantity = true
                }
            }

            this.$set(this.updatingQuantity, cartId, true)
            this.cancelUpdateQuantity(cartId)
            this.cancelFetchCart()

            if (this.updatingQuantityTimeouts[cartId]) {
                clearTimeout(this.updatingQuantityTimeouts[cartId])
            }

            this.$set(this.updatingQuantityTimeouts, cartId, setTimeout(() => {
                this.postNewQuantity(cartId, quantity, hasReachedMaxQuantity)

                this.$delete(this.updatingQuantityTimeouts, cartId)
            }, isFirstUpdate ? 0 : 650))
        },
        postNewQuantity(cartId, quantity, hasReachedMaxQuantity) {
            client
                .post(`/change-item-quantity/${cartId}`, {
                    quantity: quantity
                },{
                    cancelToken: this.updateQuantityCancelTokens[cartId].token,
                })
                .then((response) => {
                    const data = response.data

                    if (!data.status) {
                        return;
                    }

                    if (this.$refs[`cart_item_${cartId}`] && !hasReachedMaxQuantity) {
                        tippyAlert(
                            this.$refs[`cart_item_${cartId}`][0].$refs['rSpinner'],
                            data.message,
                            { theme: 'success', zIndex: '200', offsetTop: 0 },
                            10 * 1000
                        )
                    }

                    this.afterQuantityFetch(cartId)

                    if (!this.updatingQuantityTimeouts[cartId]) {
                        this.$delete(this.updatingQuantity, cartId)
                    }
                })
                .catch((error) => {
                    if (!client.isCancel(error)) {
                        this.afterQuantityFetch(cartId)
                    }
                });
        },
        afterQuantityFetch(cartId) {
            if (!this.updatingQuantityTimeouts[cartId]) {
                this.$delete(this.updatingQuantity, cartId)
            }

            if (!Object.keys(this.updatingQuantity).length) {
                this.fetchCart()
            }
        },
        handleClickOutside($event) {
            if (this.isOpen && !this.$el.contains($event.target) && !$event.target.closest('.swal2-container') && !$event.target.closest('.notify')) {
                this.toggleMinicart(false)
            }
        },
        handleMinicartClick(event) {
            if (!checkResolution('cxs;xs')) {
                event.preventDefault()
                this.toggleMinicart()
            }
        },
        toggleMinicart(bool) {
            if (typeof bool !== 'boolean') {
                bool = !this.isOpen
            }

            this.isOpen = bool

            if (bool) {
                document.addEventListener("click", this.handleClickOutside)
            } else {
                document.removeEventListener("click", this.handleClickOutside)
            }
        },
    },
    watch: {
        isOpen(value) {
            if (value) {
                showHeaderOverlay('minicart')
            } else {
                hideHeaderOverlay('minicart')
            }
        },
        isLoading(value) {
            if (!this.value && !this.isInitialized) {
                this.isInitialized = true
            }
        }
    }
};
</script>
